import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { Card, CardContent, CardHeader, CardTitle } from '../UI/card';
import { Button } from '../UI/button';
import { CheckCircle, XCircle, AlertCircle, ChevronDown, ChevronUp, Download } from 'lucide-react';

/**
 * COMPOSANT: RÉSUMÉ DES PAIEMENTS
 * Affiche le résultat du traitement : succès, lignes skippées (avertissements) et erreurs réelles.
 */

const fmt = (amount) =>
  new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';

const downloadXLSX = (filename, headers, rows) => {
  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
  // Auto column widths
  const colWidths = headers.map((h, i) => ({
    wch: Math.max(h.length, ...rows.map(r => String(r[i] ?? '').length), 10)
  }));
  ws['!cols'] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Résultats');
  XLSX.writeFile(wb, filename);
};

const Section = ({ title, icon, count, colorClass, onDownload, children }) => {
  const [open, setOpen] = useState(false);
  if (count === 0) return null;
  return (
    <Card className={`border ${colorClass.border}`}>
      <CardHeader className={`py-3 px-4 ${colorClass.header}`}>
        <CardTitle className={`text-base flex items-center justify-between ${colorClass.title}`}>
          <span
            className="flex items-center gap-2 cursor-pointer select-none flex-1"
            onClick={() => setOpen(o => !o)}
          >
            {icon}
            {title} ({count})
            {open ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </span>
          {onDownload && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 gap-1 text-xs"
              onClick={(e) => { e.stopPropagation(); onDownload(); }}
            >
              <Download className="w-3 h-3" /> CSV
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      {open && <CardContent className="p-0">{children}</CardContent>}
    </Card>
  );
};

const PaymentSummary = ({ results, validationRows = [], type = 'third-party' }) => {
  if (!results) return null;

  // En V2, chaque ligne porte sa propre période (mois/année)
  const showPeriod = type === 'bulk-v2';
  const fmtPeriod = (m, y) => {
    if (m == null && y == null) return '-';
    const mm = m != null ? String(m).padStart(2, '0') : '--';
    const yy = y != null ? String(y) : '----';
    return `${mm}/${yy}`;
  };

  const {
    totalProcessed,
    successCount,
    errorCount,
    payments = [],
    errors = []
  } = results;

  const warnings    = errors.filter(e => e.warning);
  const realErrors  = errors.filter(e => !e.warning);
  const invalidRows = validationRows.filter(r => !r.valid);

  const successRate = totalProcessed > 0
    ? ((successCount / totalProcessed) * 100).toFixed(1)
    : 0;

  const allSuccess = successCount === totalProcessed && invalidRows.length === 0;

  // ── Colonnes communes ─────────────────────────────────────────────
  const HEADERS = showPeriod
    ? ['Matricule', 'Nom', 'Prénoms', 'Produit', 'Période', 'Montant', 'Motif']
    : ['Matricule', 'Nom', 'Prénoms', 'Produit', 'Montant', 'Motif'];

  const periodFromPayment = (p) => {
    if (p.year != null || p.month != null) return fmtPeriod(p.month, p.year);
    const md = p.metadata || {};
    return fmtPeriod(md.month, md.year);
  };

  const dlProcessed = () => downloadXLSX(
    'traites.xlsx', HEADERS,
    payments.map(p => {
      const base = [
        p.mutualiste?.matriculeinterne || p.mutualiste?.matricule || '',
        p.mutualiste?.nom || '',
        p.mutualiste?.prenoms || '',
        p.produit || p.offer?.name || ''
      ];
      if (showPeriod) base.push(periodFromPayment(p));
      base.push(p.amount ?? '', p.status);
      return base;
    })
  );

  const dlIgnored = () => downloadXLSX(
    'ignores.xlsx', HEADERS,
    warnings.map(w => {
      const base = [w.matricule || '', w.nom || '', w.prenoms || '', w.produit || ''];
      if (showPeriod) base.push(fmtPeriod(w.month, w.year));
      base.push(w.montant ?? '', w.error);
      return base;
    })
  );

  const dlErrors = () => downloadXLSX(
    'erreurs.xlsx', HEADERS,
    realErrors.map(e => {
      const base = [e.matricule || '', e.nom || '', e.prenoms || '', e.produit || ''];
      if (showPeriod) base.push(fmtPeriod(e.month, e.year));
      base.push(e.montant ?? '', e.error);
      return base;
    })
  );

  const dlInvalid = () => downloadXLSX(
    'lignes_invalides.xlsx', HEADERS,
    invalidRows.map(r => {
      const base = [
        r.data?.matricule || r.data?.matriculeinterne || '',
        r.data?.nom || '',
        r.data?.prenoms || '',
        r.data?.produit || ''
      ];
      if (showPeriod) base.push(fmtPeriod(r.data?.month, r.data?.year));
      base.push(r.data?.montant || '', (r.errors || []).join(' | '));
      return base;
    })
  );

  return (
    <div className="space-y-4">

      {/* ── Bandeau résumé ── */}
      <Card className={allSuccess ? 'border-green-200 bg-green-50' : 'border-orange-200 bg-orange-50'}>
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            {allSuccess
              ? <CheckCircle className="w-10 h-10 text-green-600 shrink-0" />
              : <AlertCircle className="w-10 h-10 text-orange-600 shrink-0" />}
            <div className="flex-1">
              <p className="font-bold text-lg">
                {allSuccess ? 'Traitement terminé avec succès' : 'Traitement terminé'}
              </p>
              <p className="text-sm text-muted-foreground">
                {successCount} paiement{successCount !== 1 ? 's' : ''} traité{successCount !== 1 ? 's' : ''} sur {totalProcessed}
              </p>
            </div>
            <div className="text-right shrink-0">
              <p className="text-2xl font-bold">{successRate}%</p>
              <p className="text-xs text-muted-foreground">taux de succès</p>
            </div>
          </div>

          {/* Barre de progression */}
          <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
            <div
              className={`h-2 rounded-full transition-all ${
                successRate >= 80 ? 'bg-green-500' : successRate >= 50 ? 'bg-orange-500' : 'bg-red-500'
              }`}
              style={{ width: `${successRate}%` }}
            />
          </div>
        </CardContent>
      </Card>

      {/* ── Compteurs ── */}
      <div className="grid grid-cols-4 gap-3">
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-3 text-center">
            <CheckCircle className="w-5 h-5 text-green-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-green-700">{successCount}</p>
            <p className="text-xs text-green-600">Traités</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-3 text-center">
            <AlertCircle className="w-5 h-5 text-amber-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-amber-700">{warnings.length}</p>
            <p className="text-xs text-amber-600">Ignorés</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-3 text-center">
            <XCircle className="w-5 h-5 text-red-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-red-700">{realErrors.length}</p>
            <p className="text-xs text-red-600">Erreurs</p>
          </CardContent>
        </Card>
        <Card className="border-gray-200 bg-gray-50">
          <CardContent className="p-3 text-center">
            <XCircle className="w-5 h-5 text-gray-500 mx-auto mb-1" />
            <p className="text-2xl font-bold text-gray-700">{invalidRows.length}</p>
            <p className="text-xs text-gray-500">Invalides</p>
          </CardContent>
        </Card>
      </div>

      {/* ── Tableau uniforme réutilisable ── */}
      {[
        {
          title: 'Paiements traités',
          icon: <CheckCircle className="w-4 h-4" />,
          count: successCount,
          color: { border: 'border-green-200', header: 'bg-green-50', title: 'text-green-700', th: 'text-green-800', divider: 'divide-green-100', row: 'hover:bg-green-50' },
          onDownload: dlProcessed,
          data: payments.map(p => ({
            matricule: p.mutualiste?.matriculeinterne || p.mutualiste?.matricule || '-',
            nom: p.mutualiste?.nom || '-',
            prenoms: p.mutualiste?.prenoms || '',
            produit: p.produit || p.offer?.name || '-',
            periode: periodFromPayment(p),
            montant: p.amount != null ? fmt(p.amount) : '-',
            motif: p.receiptNumber || p.status || '-'
          }))
        },
        {
          title: 'Lignes ignorées (déjà payées)',
          icon: <AlertCircle className="w-4 h-4" />,
          count: warnings.length,
          color: { border: 'border-amber-200', header: 'bg-amber-50', title: 'text-amber-700', th: 'text-amber-800', divider: 'divide-amber-100', row: 'hover:bg-amber-50' },
          onDownload: dlIgnored,
          data: warnings.map(w => ({
            matricule: w.matricule || '-',
            nom: w.nom || '-',
            prenoms: w.prenoms || '',
            produit: w.produit || '-',
            periode: fmtPeriod(w.month, w.year),
            montant: w.montant != null ? fmt(w.montant) : '-',
            motif: w.error
          }))
        },
        {
          title: 'Erreurs de traitement',
          icon: <XCircle className="w-4 h-4" />,
          count: realErrors.length,
          color: { border: 'border-red-200', header: 'bg-red-50', title: 'text-red-700', th: 'text-red-800', divider: 'divide-red-100', row: 'hover:bg-red-50' },
          onDownload: dlErrors,
          data: realErrors.map(e => ({
            matricule: e.matricule || '-',
            nom: e.nom || '-',
            prenoms: e.prenoms || '',
            produit: e.produit || '-',
            periode: fmtPeriod(e.month, e.year),
            montant: e.montant != null ? fmt(e.montant) : '-',
            motif: e.error
          }))
        },
        {
          title: 'Lignes invalides (rejetées à la validation)',
          icon: <XCircle className="w-4 h-4" />,
          count: invalidRows.length,
          color: { border: 'border-gray-200', header: 'bg-gray-50', title: 'text-gray-700', th: 'text-gray-600', divider: 'divide-gray-100', row: 'hover:bg-gray-50' },
          onDownload: dlInvalid,
          data: invalidRows.map(r => ({
            matricule: r.data?.matricule || r.data?.matriculeinterne || '-',
            nom: r.data?.nom || '-',
            prenoms: r.data?.prenoms || '',
            produit: r.data?.produit || '-',
            periode: fmtPeriod(r.data?.month, r.data?.year),
            montant: r.data?.montant || '-',
            motif: (r.errors || []).join(' · ')
          }))
        }
      ].map(({ title, icon, count, color, onDownload, data }) => (
        <Section key={title} title={title} icon={icon} count={count}
          colorClass={{ border: color.border, header: color.header, title: color.title }}
          onDownload={onDownload}
        >
          <div className="max-h-80 overflow-y-auto">
            <table className="min-w-full text-sm">
              <thead className={`${color.header} sticky top-0 z-10`}>
                <tr>
                  {HEADERS.map(h => (
                    <th key={h} className={`px-3 py-2 text-left font-medium ${color.th} whitespace-nowrap`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className={`divide-y ${color.divider}`}>
                {data.map((row, i) => (
                  <tr key={i} className={color.row}>
                    <td className="px-3 py-2 font-mono text-xs">{row.matricule}</td>
                    <td className="px-3 py-2">{row.nom}</td>
                    <td className="px-3 py-2">{row.prenoms}</td>
                    <td className="px-3 py-2 text-xs">{row.produit}</td>
                    {showPeriod && <td className="px-3 py-2 text-xs font-mono">{row.periode}</td>}
                    <td className="px-3 py-2 text-right">{row.montant}</td>
                    <td className="px-3 py-2 text-xs">{row.motif}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Section>
      ))}

    </div>
  );
};

export default PaymentSummary;
